import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const DEFAULT_USER = {
  name: "",
  number: 0,
  weekly_usage: 5,
  yearly_usage: 50,
  more_intense_weeks: 0,
  less_intense_weeks: 0,
  mean_time_on_site: 7,
  mean_time_hand_wash: 0.5,
  mean_time_shower: 10,
  share_shower: 20,
  mean_toilet_per_stay: 2,
  mean_shower_per_stay: 0,
  mean_urinal_per_stay: 0.5,
  male_ratio: 50,
};

// Recalcule les champs dérivés à partir des champs de base — même formule que
// front/src/components/auditPages/auditSubsections/BuildingUserForm.js.
const autoCompute = (user) => ({
  ...user,
  mean_toilet_per_stay: Math.round(user.mean_time_on_site / 3 * 100) / 100,
  mean_urinal_per_stay: Math.round(user.mean_time_on_site / 3 * user.male_ratio / 100 / 2 * 100) / 100,
  mean_shower_per_stay: Math.round(user.share_shower / 100 * 0.8 * 100) / 100,
});

export default function UserCategoryModal({ buildingId, buildingData, editingUser, onClose, onSaved, onDeleted }) {
  const isEditing = !!editingUser;
  const [user, setUser] = useState(() => isEditing
    ? { ...editingUser }
    : autoCompute({ ...DEFAULT_USER, user_id: "user_" + Date.now() }));
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const patch = (key, value) => setUser(prev => autoCompute({ ...prev, [key]: value }));
  const patchRaw = (key, value) => setUser(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!user.name?.trim()) {
      toast.error("Le nom de la catégorie est obligatoire.");
      return;
    }

    const existingUsers = buildingData.building_users || [];
    const updatedUsers = isEditing
      ? existingUsers.map(u => u.user_id === user.user_id ? user : u)
      : [...existingUsers, user];
    const updatedBuildingData = { ...buildingData, building_users: updatedUsers };

    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/update_building`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedBuildingData),
      });
      if (!response.ok) {
        toast.error("Erreur lors de l'enregistrement de la catégorie.");
        return;
      }

      // Une catégorie déjà existante peut avoir des usages en place : on
      // propage les champs de taux (comme AuditPageCreationBuilding le fait)
      // pour que les usages déjà répartis reflètent le profil mis à jour.
      if (isEditing) {
        await fetch(`${process.env.REACT_APP_API_URL}/update_usages_users_by_building`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ building_id: buildingId, users: [user] }),
        });
      }

      toast.success(isEditing ? "Catégorie mise à jour." : "Catégorie créée.");
      onSaved(updatedBuildingData, user);
      onClose();
    } catch (error) {
      toast.error("Erreur de connexion à l'API.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Supprimer cette catégorie ?',
      html: `Vous êtes sur le point de supprimer <strong>${user.name || "cette catégorie"}</strong>.<br>` +
        `<span style="color:#b00;font-weight:600;">Tous les usages et lots de répartition associés seront supprimés.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#d33',
      customClass: 'swal-custom',
    });
    if (!result.isConfirmed) return;

    setDeleting(true);
    try {
      // Nettoie les usages ET les lots de répartition de cette catégorie
      // (voir le correctif de /delete_usage_by_user).
      const usagesResponse = await fetch(`${process.env.REACT_APP_API_URL}/delete_usage_by_user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ building_id: buildingId, user_id: user.user_id }),
      });
      if (!usagesResponse.ok) {
        toast.error("Erreur lors de la suppression des usages associés.");
        return;
      }

      const updatedUsers = (buildingData.building_users || []).filter(u => u.user_id !== user.user_id);
      const updatedBuildingData = { ...buildingData, building_users: updatedUsers };

      const buildingResponse = await fetch(`${process.env.REACT_APP_API_URL}/update_building`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedBuildingData),
      });
      if (!buildingResponse.ok) {
        toast.error("Erreur lors de la suppression de la catégorie.");
        return;
      }

      toast.success("Catégorie supprimée.");
      onDeleted(updatedBuildingData, user.user_id);
      onClose();
    } catch (error) {
      toast.error("Erreur de connexion à l'API.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="ceq-modal-backdrop" onClick={onClose} />
      <div className="ceq-modal" role="dialog" aria-modal="true">
        <div className="ceq-modal-header">
          <div>
            <p className="ceq-modal-header-label">Catégorie d'usager</p>
            <h2 className="ceq-modal-header-title">{isEditing ? 'Modifier' : 'Nouvelle catégorie'}</h2>
          </div>
          <button type="button" className="ceq-modal-close" onClick={onClose} aria-label="Fermer">✕</button>
        </div>

        <div className="ceq-modal-body">
          <div className="ceq-modal-fields">
            <div className="ceq-field-group ceq-field-group--full">
              <label className="ceq-field-label">Nom de la catégorie</label>
              <input className="ceq-field-input" type="text" value={user.name}
                onChange={e => patchRaw('name', e.target.value)} placeholder="Ex : Usagers bureaux" autoFocus />
            </div>
            <div className="ceq-field-group">
              <label className="ceq-field-label">Nombre d'usagers</label>
              {/* step="any" : effectif moyen ou en équivalents temps plein (ex. 12,5), comme les lots */}
              <input className="ceq-field-input" type="number" min={0} step="any" value={user.number}
                onChange={e => patchRaw('number', Number(e.target.value))} />
            </div>
            <div className="ceq-field-group">
              <label className="ceq-field-label">Temps sur site (h)</label>
              <input className="ceq-field-input" type="number" min={0} step={0.5} value={user.mean_time_on_site}
                onChange={e => patch('mean_time_on_site', Number(e.target.value))} />
            </div>
            <div className="ceq-field-group">
              <label className="ceq-field-label">Jours par semaine</label>
              <input className="ceq-field-input" type="number" min={1} max={7} value={user.weekly_usage}
                onChange={e => patchRaw('weekly_usage', Number(e.target.value))} />
            </div>
            <div className="ceq-field-group">
              <label className="ceq-field-label">Semaines par an</label>
              <input className="ceq-field-input" type="number" min={1} max={52} value={user.yearly_usage}
                onChange={e => patchRaw('yearly_usage', Number(e.target.value))} />
            </div>
          </div>

          <div className="cus-form-field" style={{ marginTop: 16 }}>
            <div className="cus-form-label-row">
              <label className="cus-form-label">Part d'usagers utilisant les douches</label>
              <span className="cus-form-value-badge">{user.share_shower}%</span>
            </div>
            <div className="cus-range-wrapper">
              <input type="range" className="cus-range-input" min={0} max={100} step={1}
                value={user.share_shower} onChange={e => patch('share_shower', Number(e.target.value))} />
              <div className="cus-range-markers"><span>0</span><span>50</span><span>100</span></div>
            </div>
          </div>

          <div className="cus-form-field" style={{ marginTop: 16 }}>
            <div className="cus-form-label-row">
              <label className="cus-form-label">Part d'hommes parmi les usagers</label>
              <span className="cus-form-value-badge">{user.male_ratio}%</span>
            </div>
            <div className="cus-range-wrapper">
              <input type="range" className="cus-range-input" min={0} max={100} step={1}
                value={user.male_ratio} onChange={e => patch('male_ratio', Number(e.target.value))} />
              <div className="cus-range-markers"><span>0</span><span>50</span><span>100</span></div>
            </div>
          </div>

          <div className="cus-form-field" style={{ marginTop: 16 }}>
            <label className="cus-form-label">Hypothèses et sources</label>
            <textarea className="cus-text-input" rows={3} value={user.notes ?? ''}
              placeholder="Ex. : effectif d'après le registre du personnel, 7 h sur site (planning du directeur)"
              onChange={e => patchRaw('notes', e.target.value)} />
          </div>

          <div className="cus-form-section cus-form-section--last" style={{ marginTop: 16 }}>
            <button type="button" className="cus-advanced-toggle" onClick={() => setShowAdvanced(p => !p)}>
              <span>{showAdvanced ? '▾' : '▸'}</span>
              <span>Informations complémentaires (calculées automatiquement, modifiables)</span>
            </button>

            {showAdvanced && (
              <div className="cus-advanced-body">
                <div className="ceq-modal-fields">
                  <div className="ceq-field-group">
                    <label className="ceq-field-label">Temps lavage mains (min)</label>
                    <input className="ceq-field-input" type="number" step={0.1} value={user.mean_time_hand_wash}
                      onChange={e => patchRaw('mean_time_hand_wash', Number(e.target.value))} />
                  </div>
                  <div className="ceq-field-group">
                    <label className="ceq-field-label">Temps douche (min)</label>
                    <input className="ceq-field-input" type="number" step={0.1} value={user.mean_time_shower}
                      onChange={e => patchRaw('mean_time_shower', Number(e.target.value))} />
                  </div>
                  <div className="ceq-field-group">
                    <label className="ceq-field-label">Passages WC / présence</label>
                    <input className="ceq-field-input" type="number" step={0.1} value={user.mean_toilet_per_stay}
                      onChange={e => patchRaw('mean_toilet_per_stay', Number(e.target.value))} />
                  </div>
                  <div className="ceq-field-group">
                    <label className="ceq-field-label">Passages urinoir / présence</label>
                    <input className="ceq-field-input" type="number" step={0.1} value={user.mean_urinal_per_stay}
                      onChange={e => patchRaw('mean_urinal_per_stay', Number(e.target.value))} />
                  </div>
                  <div className="ceq-field-group">
                    <label className="ceq-field-label">Douches / présence</label>
                    <input className="ceq-field-input" type="number" step={0.1} value={user.mean_shower_per_stay}
                      onChange={e => patchRaw('mean_shower_per_stay', Number(e.target.value))} />
                  </div>
                  <div className="ceq-field-group">
                    <label className="ceq-field-label">Sem. d'activité intense</label>
                    <input className="ceq-field-input" type="number" min={0} value={user.more_intense_weeks}
                      onChange={e => patchRaw('more_intense_weeks', Number(e.target.value))} />
                  </div>
                  <div className="ceq-field-group">
                    <label className="ceq-field-label">Sem. d'activité réduite</label>
                    <input className="ceq-field-input" type="number" min={0} value={user.less_intense_weeks}
                      onChange={e => patchRaw('less_intense_weeks', Number(e.target.value))} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="ceq-modal-footer">
          {isEditing && (
            <button type="button" className="cus-dist-danger-btn" style={{ marginRight: 'auto' }}
              onClick={handleDelete} disabled={saving || deleting}>
              {deleting ? 'Suppression…' : 'Supprimer cette catégorie'}
            </button>
          )}
          <button type="button" className="ceq-modal-cancel" onClick={onClose} disabled={saving || deleting}>Annuler</button>
          <button type="button" className="ceq-modal-save" onClick={handleSave} disabled={saving || deleting}>
            {saving ? 'Enregistrement…' : isEditing ? 'Enregistrer' : 'Créer la catégorie'}
          </button>
        </div>
      </div>
    </>
  );
}
